import { useSmartReply } from '@copilot/common/hooks/smartReply/hooks/useSmartReply';
import { InboxMessageType } from '@copilot/common/pages/inbox/data/types';
import { OrganizationSelectors } from '@copilot/common/store/selectors/organization';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { isEmpty, isNil } from 'lodash';
import delay from 'lodash/delay';
import {
    CUSTOM_PROMPT_KEY,
    PromptSubmissionData,
} from '@copilot/common/hooks/smartReply/smartReplyTypes';
import { getSmartReplyPromptCategory } from '@copilot/common/components/forms/messageForm/smartReply/smartReplyUtils';
import { useSmartReplyPromptPresets } from '@copilot/common/hooks/smartReply/smartReplyPromptPresets/useSmartReplyPromptPresetContext';
import { getOperationStatus } from './smartReplyUtils';
/**
 * Hook to get all functionality for smart reply
 */
export function useSmartReplyManagement(replyText: string, message: InboxMessageType) {
    const organizationId = useSelector(OrganizationSelectors.getActiveOrganizationId);
    const [
        smartReplyResult,
        { writeSmartReply, regenerateSmartReply, editSmartReply, canUseCommunicationStyles },
    ] = useSmartReply(
        organizationId,
        message.orgMemberId,
        message.contactId,
        message.communicationStyles
    );
    const [isAssistantActive, setIsAssistantActive] = useState<boolean>(false);
    const [hasGenerated, setHasGenerated] = useState<boolean>(false);
    const [isStatusVisible, setStatusVisibility] = useState<boolean>(false);
    const [lastUsedPrompt, setLastUsedPrompt] = useState<PromptSubmissionData>();
    const { operationStatus, operationMessage } = getOperationStatus(smartReplyResult);
    const { getByKey: getPromptByKey } = useSmartReplyPromptPresets();

    useEffect(() => {
        if (isEmpty(smartReplyResult.data)) return;
        setHasGenerated(true);
        const delayId = delay(() => setStatusVisibility(false), 5000);
        return () => clearTimeout(delayId);
    }, [smartReplyResult.data]);

    useEffect(() => {
        if (smartReplyResult.loading) setStatusVisibility(true);
    }, [smartReplyResult.loading]);

    /**
     * Function called when user selects a preset prompt
     * @param promptKey
     */
    async function onSelectPreset(promptKey: string, includePersonalizedInsights: boolean) {
        setIsAssistantActive(false);
        const prompt = getPromptByKey(promptKey);
        if (isNil(prompt)) throw new Error(`Prompt with key ${promptKey} not found`);

        try {
            if (prompt.action === 'EDIT') {
                setLastUsedPrompt({
                    promptId: prompt.id,
                    instruction: undefined,
                    actionType: 'EDIT',
                });
                await editSmartReply(replyText, prompt.id, undefined, includePersonalizedInsights);
            } else {
                setLastUsedPrompt({
                    promptId: prompt.id,
                    instruction: undefined,
                    actionType: 'WRITE',
                });
                await writeSmartReply(prompt.id, undefined, includePersonalizedInsights);
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Function called when user creates a custom prompt
     * @param prompt
     */
    async function onSubmitCustomPrompt(
        prompt: string,
        isEditing: boolean,
        includePersonalizedInsights: boolean
    ) {
        setIsAssistantActive(false);
        try {
            if (isEditing) {
                setLastUsedPrompt({
                    promptId: CUSTOM_PROMPT_KEY,
                    instruction: prompt,
                    actionType: 'EDIT',
                });
                await editSmartReply(
                    replyText,
                    CUSTOM_PROMPT_KEY,
                    prompt,
                    includePersonalizedInsights
                );
            } else {
                setLastUsedPrompt({
                    promptId: CUSTOM_PROMPT_KEY,
                    instruction: prompt,
                    actionType: 'WRITE',
                });

                await writeSmartReply(CUSTOM_PROMPT_KEY, prompt, includePersonalizedInsights);
            }
        } catch (err) {
            console.error(err);
        }
    }

    return {
        smartReplyResult,
        lastUsedPrompt,
        writeSmartReply,
        regenerateSmartReply,
        editSmartReply,
        isAssistantActive,
        setIsAssistantActive,
        hasGenerated,
        isStatusVisible,
        operationStatus,
        operationMessage,
        onSelectPreset,
        onSubmitCustomPrompt,
        smartReplyPromptCategory: getSmartReplyPromptCategory(message),
        canUseCommunicationStyles,
    };
}
